import { autoAnimatePlugin } from '@formkit/auto-animate/vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { configureEcho } from '@laravel/echo-vue';
import axios from 'axios';
import { ConfigProvider } from 'radix-vue';
import { createSSRApp, h } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { ZiggyVue } from 'ziggy-js';
import { useActivity } from './composables/useActivity';
import { initializeTheme } from './composables/useAppearance';
import { identifyUser, initPostHog, registerNavigationListener } from './composables/usePostHog';
import config from './config';
import { ActivityAction } from './config/activityActions';
import { isMarketingPage } from './config/constants/seo';
import i18n from './locale/i18n';
import { markAxiosRequestsAsAjax } from './utils/axiosDefaults';
import { formatDocumentTitle } from './utils/documentTitle';
import { THIRD_PARTY_SCRIPT_MESSAGES, THIRD_PARTY_SCRIPT_URLS, isReportableError } from './utils/errorReporting';
import { registerGoogleAnalyticsPageViews } from './utils/googleAnalytics';
import { runWhenIdle } from './utils/idle';
import { createNetworkErrorHandler } from './utils/inertiaNetworkError';
import { createResponseFlusher, createVisitFlusher } from './utils/prefetchInvalidation';
import { installGlobalRouteFromDocument } from './utils/ziggyRuntime';

// The route table arrives as the `ziggy` shared prop (once per session);
// there is no `@routes` script in the document any more. `route()` has to
// exist at MODULE scope, before Inertia resolves the first page component:
// that import evaluates modules such as config/constants/navigation.ts,
// which build their hrefs with `route()` while loading — well before the
// `setup()` callback below runs.
const ziggy = installGlobalRouteFromDocument();

// Before any component can start a background poll — see markAxiosRequestsAsAjax.
markAxiosRequestsAsAjax(axios);

configureEcho({
    broadcaster: 'reverb',
    key: config.REVERB_APP_KEY,
    wsHost: config.REVERB_HOST,
    wsPort: config.REVERB_PORT,
    wssPort: config.REVERB_PORT,
    forceTLS: config.REVERB_SCHEME === 'https',
    enabledTransports: ['ws', 'wss'],
});

createInertiaApp({
    title: (title) => formatDocumentTitle(title, config.APP_NAME),
    pages: './pages',
    setup({ el, App, props, plugin }) {
        // `scrollBody: false` stops Radix compensating for the scrollbar it hides
        // when a modal overlay opens. `html { scrollbar-gutter: stable }` in
        // app.css already keeps that width reserved, so the padding Radix would
        // otherwise add to the body is a second correction for a shift that no
        // longer happens — and shifts the page sideways in its own right.
        //
        // createSSRApp, not createApp: the marketing pages arrive
        // server-rendered (see ssr/renderPage.ts) and must hydrate the
        // existing markup rather than replace it. On every other page the
        // container is empty and Vue simply performs a normal mount.
        const app = createSSRApp({ render: () => h(ConfigProvider, { scrollBody: false }, () => h(App, props)) })
            .use(plugin)
            // The same config as the global `route()` above, for templates.
            .use(ZiggyVue, ziggy ?? undefined)
            .use(VueDOMPurifyHTML, { enableSSRPropsSupport: true })
            .use(i18n)
            .use(autoAnimatePlugin);

        const initialPage = props.initialPage as { component?: string; props?: { auth?: { user?: unknown; tenant?: unknown } } };
        const marketing = isMarketingPage(initialPage.component);

        /*
         * PostHog and Sentry together cost ~600 ms of main-thread time during
         * load (session recorder, surveys bundle, browser tracing). Inside the
         * application that is paid once per session by someone who is about
         * to work for an hour. On a marketing page it is paid by every
         * visitor before they can read the hero, and it is most of the page's
         * blocking time — so there it starts once the page is idle, without
         * session recording or surveys, which record anonymous visitors to no
         * purpose.
         */
        const startTelemetry = async () => {
            initPostHog(marketing ? { sessionRecording: false, surveys: false } : {});

            // Imported on demand so the 44 KB Sentry chunk is fetched after
            // the page's own code rather than preloaded ahead of it.
            const Sentry = await import('@sentry/vue');
            const { browserTracingIntegration } = Sentry;

            Sentry.init({
                app,
                dsn: config.SENTRY_DSN,
                environment: config.SENTRY_ENV,
                release: config.SENTRY_RELEASE,
                integrations: [browserTracingIntegration()],
                // Errors thrown by scripts that are not ours (in-app browser
                // bridges) — see THIRD_PARTY_SCRIPT_URLS for the list and why,
                // and THIRD_PARTY_SCRIPT_MESSAGES for the ones injected inline
                // under our own URL, which only the message can identify.
                denyUrls: [...THIRD_PARTY_SCRIPT_URLS],
                ignoreErrors: [...THIRD_PARTY_SCRIPT_MESSAGES],
                /* eslint-disable security/detect-unsafe-regex -- matched only against the app's OWN outgoing request URLs (Sentry trace propagation), never attacker input; the `.`-anchored subdomain groups are not catastrophically ambiguous. */
                tracePropagationTargets: [
                    // same-origin, excluding static assets
                    /^\/(?!.*\.(css|js|png|jpe?g|svg|gif|ico|woff2?|map)$)/i,
                    // localhost & subdomains with optional ports
                    /^https?:\/\/([a-z0-9-]+\.)*localhost(:\d+)?\//i,
                    // tourium-staging.com and all tenant subdomains
                    /^https?:\/\/([a-z0-9-]+\.)*tourium-staging\.com\//i,
                ],
                /* eslint-enable security/detect-unsafe-regex */
                tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
            });

            registerNavigationListener();

            const user = initialPage.props?.auth?.user;

            if (user) {
                identifyUser(user as Parameters<typeof identifyUser>[0], initialPage.props?.auth?.tenant as Parameters<typeof identifyUser>[1]);
            }
        };

        if (marketing) {
            runWhenIdle(startTelemetry);
        } else {
            startTelemetry();
        }

        /*
         * GA4 page views for Inertia navigations. Registered eagerly rather
         * than inside `startTelemetry`, which is idle-deferred on marketing
         * pages: the gtag snippet is already in the document by then, and a
         * visitor who navigates before idle would otherwise go unrecorded.
         *
         * Guarded on `marketing` because the snippet is only emitted for those
         * pages — with no tag loaded there is nothing to report to, and
         * attaching a listener that can never fire just costs a closure.
         */
        if (marketing) {
            registerGoogleAnalyticsPageViews();
        }

        // --- Unified logging: observational global error net ---------------------
        // Captures ONLY uncaught technical errors for developers. It never alters the
        // user-facing error display (toasts/flash/form errors are unaffected) and
        // never swallows — it forwards to PostHog and lets the error propagate.
        const reportUncaught = (message: string, context: Record<string, unknown>, filename?: string) => {
            if (!isReportableError(message, filename)) {
                return;
            }

            try {
                const { activity } = useActivity();
                activity(ActivityAction.FrontendError).error(undefined, message).context(context).log();
            } catch {
                // Logging must never break the app.
            }
        };

        const previousErrorHandler = app.config.errorHandler;
        app.config.errorHandler = (err: unknown, instance: ComponentPublicInstance | null, info: string) => {
            reportUncaught(err instanceof Error ? err.message : String(err), { source: 'vue', info });

            if (typeof previousErrorHandler === 'function') {
                previousErrorHandler(err, instance, info);
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('error', (event) => {
                reportUncaught(
                    event.message,
                    {
                        source: 'window.onerror',
                        filename: event.filename,
                        lineno: event.lineno,
                        colno: event.colno,
                    },
                    event.filename,
                );
            });
            window.addEventListener('unhandledrejection', (event) => {
                const reason = event.reason;
                reportUncaught(reason instanceof Error ? reason.message : String(reason), { source: 'unhandledrejection' });
            });
        }

        /*
         * Report only the HTTP responses that mean something BROKE (audit
         * WF-20).
         *
         * Every `httpException` used to be reported, including the routine
         * domain refusals the product exists to make: unconfirmed accounting
         * settings (422), a blended-VAT line with no amount (422), a second tax
         * invoice for one supply (422), a permission the user does not hold
         * (403), a record scoped to another brand (404). Those are the system
         * working, shown to the user with a sentence they can act on, and they
         * buried the genuine 500s under a stream of correct behaviour.
         *
         * 4xx below 500 is left to the screen that provoked it; a 5xx (and the
         * 419 that means the session silently expired mid-form) is still ours.
         */
        router.on('httpException', (event) => {
            const status = event.detail.response.status;

            if (status < 500 && status !== 419) {
                return;
            }

            reportUncaught('Inertia HTTP exception', { source: 'inertia.httpException', status });
        });

        // A visit that never reached the server is reported, shown to the user
        // and cancelled here — see createNetworkErrorHandler for why the
        // cancelling matters.
        router.on('networkError', createNetworkErrorHandler(reportUncaught));

        // A save must not leave a prefetched page showing what was there before
        // it — see prefetchInvalidation for the whole story.
        router.on(
            'finish',
            createVisitFlusher(() => router.flushAll()),
        );
        axios.interceptors.response.use(createResponseFlusher(() => router.flushAll()));
        // -------------------------------------------------------------------------

        app.mount(el!);
    },
    // The bar is the only sign a slow page visit has started (a menu item that
    // navigates closes at once), so it takes the brand colour: the old grey was
    // easy to miss and a two-second load read as "nothing happened".
    progress: {
        color: 'hsl(var(--primary))',
    },
});

initializeTheme();
